/* ==========================================================================
   Desktop Sticky Cart — fees + total module (parallel build, same backend).

   Replicates the existing cart page's price logic:
     • POST /order/fees  { cart_items:[{tags}], payment_mode:'PREPAID', cart_total }
       → response keyed by fee type (DELIVERY_FEE, PLATFORM_FEE, …), each
         { fee, max_fee, display_text, helper_text } in RUPEES.
     • Renders the fee rows into the Price Details card.
     • Total Amount = subtotal + Σ fees − coupon discount; savings = MRP − total.
     • Persists allFeesData + finalTotalAmount (same sessionStorage keys the
       existing flow / order payload read).

   subtotal / mrp are read from the content root in the currency subunit (paise).
   Exposes window.StickyCartFees.apply(scope).
   ========================================================================== */
(function () {
  'use strict';

  var FEES_URL = 'https://supertails-backend.el.r.appspot.com/order/fees';

  // True ONLY while the price for the CURRENT cart state hasn't settled yet (first load, or the
  // cart changed) — the controller checks this before advancing to Review so the user gets a
  // loader instead of a half-computed total. It is NOT set for routine re-fetches of an unchanged
  // cart (those keep showing a valid total), so Proceed stays instant once the price has loaded.
  var feesPending = false;
  var settledSig = null; // fee-input signature of the last settled total

  function formatINR(rupees) {
    var n = Math.round(rupees || 0);
    try { return '₹' + n.toLocaleString('en-IN'); } catch (e) { return '₹' + n; }
  }

  function num(el, attr) {
    return el ? parseInt(el.getAttribute(attr), 10) || 0 : 0;
  }

  function readTags(scope) {
    var el = scope.querySelector('[data-st-fees-tags]');
    if (!el) return [];
    try { return JSON.parse(el.textContent) || []; } catch (e) { return []; }
  }

  function couponDiscountRupees() {
    if (!localStorage.getItem('discountCode')) return 0;
    var d = parseInt(localStorage.getItem('disc_price'), 10);
    return isNaN(d) ? 0 : d;
  }

  function apply(scope) {
    scope = scope || document;
    var root = scope.querySelector('[data-st-content-root]');
    var feesContainer = scope.querySelector('[data-st-fees]');
    if (!root) return;

    var subtotalRupees = num(root, 'data-subtotal') / 100;
    var mrpRupees = num(root, 'data-mrp') / 100;

    var body = {
      cart_items: readTags(scope),
      payment_mode: 'PREPAID',
      cart_total: subtotalRupees
    };

    // Pending only when this exact cart state hasn't settled before (first load / cart changed);
    // a re-fetch of an already-settled cart keeps showing the valid total, so don't block Proceed.
    var sig = JSON.stringify(body);
    feesPending = (sig !== settledSig);
    fetch(FEES_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    })
      .then(function (r) { return r.json(); })
      .then(function (result) {
        var feesData = result && result.status === 200 && result.response ? result.response : {};
        renderAndFinalize(scope, feesContainer, root, feesData, subtotalRupees, mrpRupees, sig);
      })
      .catch(function (err) {
        if (window.console) console.error('[StickyCart] fees', err);
        // API failed → still show the default delivery fee + a correct total.
        renderAndFinalize(scope, feesContainer, root, {}, subtotalRupees, mrpRupees, sig);
      });
  }

  // Always surface a Delivery Fee row: use the /order/fees value when present, otherwise the
  // server-computed default (₹49 base, FREE above the free-shipping threshold — emitted as
  // data-delivery-fee / data-delivery-mrp by sections/sticky-cart-drawer.liquid, mirroring
  // the existing cart's cart-price.liquid logic).
  function ensureDeliveryFee(feesData, root) {
    feesData = feesData || {};
    if (feesData.DELIVERY_FEE) return feesData; // /order/fees already provided it → use that
    // Else inject the default the section computed (data-delivery-fee = 0 free / 4900 charged).
    // renderFees sorts by FEE_ORDER, so insertion order here doesn't matter.
    feesData.DELIVERY_FEE = {
      fee: Math.round(num(root, 'data-delivery-fee') / 100),
      max_fee: '49',
      display_text: 'Delivery Fee',
      helper_text: 'Free shipping above ₹499.'
    };
    return feesData;
  }

  // Render the fee rows + recompute the total. Single path for both API success and failure.
  function renderAndFinalize(scope, container, root, feesData, subtotalRupees, mrpRupees, sig) {
    feesData = ensureDeliveryFee(feesData, root);
    renderFees(container, feesData);

    var totalFees = 0;
    var allFeesData = {};
    Object.keys(feesData).forEach(function (key) {
      var fee = feesData[key];
      if (typeof fee.fee === 'number') totalFees += fee.fee;
      allFeesData[key.toLowerCase()] = {
        name: fee.display_text || key.replace(/_/g, ' '),
        fee: typeof fee.fee === 'number' && fee.fee ? fee.fee : 'FREE',
        max_fee: fee.max_fee || '',
        helper_text: fee.helper_text || ''
      };
    });

    try { sessionStorage.setItem('allFeesData', JSON.stringify(allFeesData)); } catch (e) {}
    finalize(scope, subtotalRupees, totalFees, mrpRupees, sig);
  }

  // Fixed Figma order: Delivery Fee, then Platform Fee, then any other fees the API returns.
  var FEE_ORDER = ['DELIVERY_FEE', 'PLATFORM_FEE'];

  var INFO_SVG3 =
    '<svg width="11" height="12" viewBox="0 0 11 12" fill="none" xmlns="http://www.w3.org/2000/svg">' +
      '<path d="M5.53045 1C2.7637 1 0.5 3.2637 0.5 6.03045C0.5 8.79719 2.7637 11.0609 5.53045 11.0609C8.29719 11.0609 10.5609 8.79719 10.5609 6.03045C10.5609 3.2637 8.29719 1 5.53045 1ZM5.53045 10.0548C3.31705 10.0548 1.50609 8.24384 1.50609 6.03045C1.50609 3.81705 3.31705 2.00609 5.53045 2.00609C7.74384 2.00609 9.5548 3.81705 9.5548 6.03045C9.5548 8.24384 7.74384 10.0548 5.53045 10.0548Z" fill="#6D6D6D" />' +
      '<path d="M5.52763 4.86572C5.21574 4.86572 5.00781 5.07253 5.00781 5.38274V7.96783C5.00781 8.27804 5.21574 8.48485 5.52763 8.48485C5.83951 8.48485 6.04744 8.27804 6.04744 7.96783V5.38274C6.04744 5.07253 5.83951 4.86572 5.52763 4.86572Z" fill="#6D6D6D" />' +
      '<path d="M5.52763 4.47544C5.81471 4.47544 6.04744 4.24397 6.04744 3.95842C6.04744 3.67288 5.81471 3.44141 5.52763 3.44141C5.24054 3.44141 5.00781 3.67288 5.00781 3.95842C5.00781 4.24397 5.24054 4.47544 5.52763 4.47544Z" fill="#6D6D6D" />' +
    '</svg>'

  function renderFees(container, feesData) {
    if (!container) return;
    var keys = FEE_ORDER.filter(function (k) { return feesData[k]; })
      .concat(Object.keys(feesData).filter(function (k) { return FEE_ORDER.indexOf(k) < 0; }));
    if (!keys.length) {
      container.innerHTML = '';
      return;
    }
    container.innerHTML = keys.map(function (key) {
      var fee = feesData[key];
      var name = fee.display_text || prettify(key);
      var isFree = !fee.fee; // 0 / FREE
      var right;
      if (isFree) {
        right =
          (fee.max_fee ? '<s class="st-cart__bill-strike">₹' + fee.max_fee + '</s> ' : '') +
          '<span class="st-cart__bill-free">FREE</span>';
      } else {
        right =
          (fee.max_fee ? '<s class="st-cart__bill-strike">₹' + fee.max_fee + '</s> ' : '') +
          '₹' + fee.fee;
      }
      var label = escapeHtml(name) + (fee.helper_text
        ? ' <span class="st-cart__bill-info" tabindex="0" role="button" aria-label="' + escapeHtml(fee.helper_text) +
          '" data-st-tooltip="' + escapeHtml(fee.helper_text) + '">' + INFO_SVG3 + '</span>'
        : '');
      return '<div class="st-cart__bill-row"><span>' + label + '</span><span>' + right + '</span></div>';
    }).join('');
  }

  function finalize(scope, subtotalRupees, totalFees, mrpRupees, sig) {
    var coupon = couponDiscountRupees();
    var total = subtotalRupees + totalFees - coupon;
    if (total < 0) total = 0;
    // Savings counts only Discount on MRP (mrp − subtotal) + Coupon Discount — delivery /
    // platform fees are NOT netted into it.
    var savings = (mrpRupees - subtotalRupees) + coupon;
    if (savings < 0) savings = 0;

    setText(scope, '[data-st-bill-total-value]', formatINR(total));
    setText(scope, '[data-st-footer-price]', formatINR(total));

    var savingsEl = scope.querySelector('[data-st-bill-savings]');
    if (savingsEl) {
      if (savings > 0) {
        savingsEl.textContent = "🥳 You're saving " + formatINR(savings) + ' on this order';
        savingsEl.hidden = false;
      } else {
        savingsEl.hidden = true;
      }
    }

    try {
      sessionStorage.setItem('finalTotalAmount', JSON.stringify(total));
    } catch (e) {}

    // Total is now final for this cart state → record it so routine re-fetches don't re-block.
    if (sig != null) settledSig = sig;
    feesPending = false;
    try { document.dispatchEvent(new Event('stcart:pricing-updated')); } catch (e) {}
  }

  function prettify(key) {
    return key.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, function (c) { return c.toUpperCase(); });
  }

  function setText(scope, sel, val) {
    var el = scope.querySelector(sel);
    if (el) el.textContent = val;
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
    });
  }

  window.StickyCartFees = { apply: apply, isPending: function () { return feesPending; } };
})();
